import type { UrlChangeFilter, UrlListStatusFilter } from '../api/types';
import type { StatsRange } from './job-range';

export type UrlsDrilldown = {
  status?: UrlListStatusFilter;
  coverage?: string;
  days?: StatsRange;
};

export type ChangesDrilldown = {
  filter?: UrlChangeFilter;
  days?: StatsRange;
};

const STATUS_VALUES = new Set<string>([
  'all',
  'indexed',
  'not_indexed',
  'error',
  'never_inspected',
  'status_changed',
]);

const CHANGE_FILTER_VALUES = new Set<string>([
  'all',
  'became_indexed',
  'became_not_indexed',
  'error',
  'other',
]);

const DAYS_VALUES = new Set([1, 7, 14, 30]);

export function parseUrlListStatus(
  value: string | null,
): UrlListStatusFilter {
  if (value && STATUS_VALUES.has(value)) return value as UrlListStatusFilter;
  return 'all';
}

export function parseUrlChangeFilter(value: string | null): UrlChangeFilter {
  if (value && CHANGE_FILTER_VALUES.has(value)) {
    return value as UrlChangeFilter;
  }
  return 'all';
}

export function parseStatsDaysParam(
  value: string | null,
): StatsRange | undefined {
  if (!value) return undefined;
  const n = Number(value);
  if (DAYS_VALUES.has(n)) return n as StatsRange;
  return undefined;
}

export function urlsPath(
  params: UrlsDrilldown & { base?: string } = {},
): string {
  const search = new URLSearchParams();
  if (params.status && params.status !== 'all') {
    search.set('status', params.status);
  }
  if (params.coverage) search.set('coverage', params.coverage);
  if (params.days) search.set('days', String(params.days));
  const q = search.toString();
  const base = params.base ?? '/indexer/urls';
  return q ? `${base}?${q}` : base;
}

export function changesPath(
  params: ChangesDrilldown & { base?: string } = {},
): string {
  const search = new URLSearchParams();
  if (params.filter && params.filter !== 'all') {
    search.set('filter', params.filter);
  }
  if (params.days) search.set('days', String(params.days));
  const q = search.toString();
  const base = params.base ?? '/indexer/changes';
  return q ? `${base}?${q}` : base;
}

/** Map overview inventory donut / KPI ids to URL list status. */
export function inventorySliceToStatus(
  sliceId: string,
): UrlListStatusFilter | null {
  switch (sliceId) {
    case 'indexed':
      return 'indexed';
    case 'notIndexed':
    case 'not-indexed':
      return 'not_indexed';
    case 'error':
      return 'error';
    case 'never':
      return 'never_inspected';
    default:
      return null;
  }
}

/** Map overview changes donut ids to changes page filter. */
export function changesSliceToFilter(sliceId: string): UrlChangeFilter | null {
  switch (sliceId) {
    case 'becameIndexed':
      return 'became_indexed';
    case 'becameNotIndexed':
      return 'became_not_indexed';
    case 'error':
      return 'error';
    case 'other':
      return 'other';
    default:
      return null;
  }
}

export function coverageLabelForChip(coverage: string): string {
  if (coverage === '__never_inspected__') return 'بدون بازرسی در بازه';
  if (coverage === '__unknown__') return 'نامشخص';
  return coverage;
}
